
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ToolbarButton } from '@ohif/ui';
import { utils } from '@ohif/core';
import Simple2DMPRLayout from '../Simple2DMPRLayout';
import { buildMPR, isCached } from '../mprBuilder';
import { mprState } from '../Simple2DMPRViewport';

const { studyMetadataManager } = utils;

function _isReconstructable(vsd = {}, idx) {
  if (!vsd[idx]) return false;
  const { displaySetInstanceUID: uid, StudyInstanceUID: suid } = vsd[idx];
  const study = studyMetadataManager.all().find(s => s.studyInstanceUID === suid);
  if (!study) return false;
  const ds = study._displaySets.find(d => d.displaySetInstanceUID === uid);
  return !!(ds && ds.isReconstructable);
}

function getImageIds(vsd, idx) {
  if (!vsd || !vsd[idx]) return [];
  const { displaySetInstanceUID: uid, StudyInstanceUID: suid } = vsd[idx];
  const study = studyMetadataManager.all().find(s => s.studyInstanceUID === suid);
  if (!study) return [];
  const ds = study._displaySets.find(d => d.displaySetInstanceUID === uid);
  if (!ds || !ds.images) return [];
  const ids = [];
  ds.images.forEach(img => {
    const n = img.numFrames;
    if (n > 1) for (let i = 0; i < n; i++) ids.push(img.getImageId(i));
    else ids.push(img.getImageId());
  });
  return ids;
}

export default function VTKMPRToolbarButton({
  parentContext, toolbarClickCallback, button, activeButtons, isActive,
}) {
  const { id, label, icon } = button;
  const [mprOpen,  setMprOpen]  = useState(false);
  const [imageIds, setImageIds] = useState([]);
  const [status,   setStatus]   = useState('idle');
  const buildingFor = useRef(null);

  const { viewportSpecificData: vsd, activeViewportIndex: idx } = useSelector(state => ({
    viewportSpecificData: state.viewports.viewportSpecificData,
    activeViewportIndex:  state.viewports.activeViewportIndex,
  }));

  const getUID = () => vsd && vsd[idx] ? vsd[idx].displaySetInstanceUID : null;

  const tryBuild = uid => {
    if (!uid) return;
    if (isCached(uid)) { setStatus('ready'); return; }
    if (buildingFor.current === uid) return;
    const ids = getImageIds(vsd, idx);
    if (!ids.length) return;
    buildingFor.current = uid;
    setStatus('building'); // hidden during build — no glitch
    Object.assign(mprState, { axialIdx:0, corIdx:0, sagIdx:0, depth:1 });
    buildMPR(ids, uid).then(ok => {
      if (buildingFor.current !== uid) return;
      setStatus(ok ? 'ready' : 'idle');
    });
  };

  useEffect(() => {
    if (!window.__mprLoadProgress) window.__mprLoadProgress = {};
    const handler = e => {
      const { progressId, progressData } = e.detail || {};
      if (!progressData) return;
      const uid = getUID();
      if (!uid || progressId !== 'StackProgress:' + uid) return;
      const pct = progressData.percentComplete || 0;
      window.__mprLoadProgress[progressId] = pct;
      if (pct >= 100) tryBuild(uid);
    };
    document.addEventListener('StudyLoadingListenerEvents.OnProgress', handler);
    const uid = getUID();
    if (uid) {
      const pct = (window.__mprLoadProgress || {})['StackProgress:' + uid] || 0;
      if (pct >= 100 || isCached(uid)) tryBuild(uid);
      else setStatus('idle');
    }
    return () => document.removeEventListener('StudyLoadingListenerEvents.OnProgress', handler);
  }, [idx, vsd]);

  useEffect(() => {
    // Close MPR immediately when active viewport changes
    // Clear imageIds too so CornerstoneViewport never gets undefined imageId
    setMprOpen(false);
    setImageIds([]);
    setStatus('idle');
    buildingFor.current = null;
    // Reset shared mprState so next open starts fresh
    Object.assign(mprState, { axialIdx:0, corIdx:0, sagIdx:0, depth:1, rows:1, columns:1 });
    // Clear imageId cache so new series gets fresh ids
    if (window._imageIdCache) window._imageIdCache = {};
  }, [idx]);

  const handleClick = () => {
    if (status !== 'ready') return;
    setImageIds(getImageIds(vsd, idx));
    setMprOpen(v => !v);
  };

  // Only render when ready — completely absent during build
  // isReconstructable check removed — allow MPR for all series
  if (status !== 'ready') return null;

  const uid = getUID();

  return (
    <>
      <ToolbarButton
        key={id}
        label={label}
        icon={icon}
        onClick={handleClick}
        isActive={isActive || mprOpen}
      />
      {mprOpen && imageIds && imageIds.length > 0 && uid && (
        <Simple2DMPRLayout
          imageIds={imageIds}
          displaySetUID={uid}
          onClose={() => setMprOpen(false)}
        />
      )}
    </>
  );
}

VTKMPRToolbarButton.propTypes = {
  parentContext:        PropTypes.object.isRequired,
  toolbarClickCallback: PropTypes.func.isRequired,
  button:               PropTypes.object.isRequired,
  activeButtons:        PropTypes.array.isRequired,
  isActive:             PropTypes.bool,
  className:            PropTypes.string,
};
